import type { User } from '@ifixit/auth-sdk';
import type { CustomerAccessToken } from '@ifixit/shopify-storefront-client';
import Cookies from 'js-cookie';
import { z } from 'zod';

export function getStoredCustomerAccessToken({
   storeCode,
   user,
}: {
   storeCode: string;
   user: User;
}): CustomerAccessToken | null {
   const userId = user.id;
   const cartCookie = getCartCookie({ storeCode, userId });
   return cartCookie[storeCode]?.customerAccessToken ?? null;
}

export function clearStoredCustomerAccessToken({
   storeCode,
   user,
}: {
   storeCode: string;
   user: User;
}): void {
   const userId = user.id;
   const cartCookie = getCartCookie({ storeCode, userId });
   if (cartCookie[storeCode].customerAccessToken) {
      cartCookie[storeCode].customerAccessToken = null;
      setCartCookie({ cartCookie, userId });
   }
}

export function getStoredCartId({
   user,
   storeCode,
}: {
   user: User | null;
   storeCode: string;
}): string | null {
   const userId = user?.id ?? null;
   const cartCookie = getCartCookie({ storeCode, userId });
   return cartCookie[storeCode]?.cartId ?? null;
}

export function clearStoredCartId({
   storeCode,
   user,
}: {
   storeCode: string;
   user: User | null;
}): void {
   const userId = user?.id ?? null;
   const cartCookie = getCartCookie({ storeCode, userId });
   if (cartCookie[storeCode].cartId) {
      cartCookie[storeCode].cartId = null;
      setCartCookie({ cartCookie, userId });
   }
}

type StoreCodeMaybeUserId = {
   storeCode: string;
   userId: number | null;
};
type StoreCodeUserId = {
   storeCode: string;
   userId: number;
};

export function updateCartId({
   cartId,
   storeCode,
   userId,
}: { cartId: string } & StoreCodeMaybeUserId) {
   const cartCookie = getCartCookie({ storeCode, userId });
   cartCookie[storeCode].cartId = cartId;
   setCartCookie({ cartCookie, userId });
}

export function updateCustomerAccessToken({
   customerAccessToken,
   storeCode,
   userId,
}: { customerAccessToken: CustomerAccessToken } & StoreCodeUserId) {
   const cartCookie = getCartCookie({ storeCode, userId });
   cartCookie[storeCode].customerAccessToken = customerAccessToken;
   setCartCookie({ cartCookie, userId });
}

const CustomerAccessTokenSchema = z.object({
   accessToken: z.string(),
   expiresAt: z.string(),
});
const CartCookieSchema = z.record(
   z.string(),
   z.object({
      cartId: z.string().nullable(),
      customerAccessToken: CustomerAccessTokenSchema.nullable(),
   })
);
type CartCookie = z.infer<typeof CartCookieSchema>;

function getCartCookie({ storeCode, userId }: StoreCodeMaybeUserId) {
   const existing = readCartCookie({ userId }) ?? {};
   if (!existing[storeCode]) {
      existing[storeCode] = {
         cartId: null,
         customerAccessToken: null,
      };
   }
   return existing;
}

function readCartCookie({ userId }: { userId: number | null }): CartCookie | null {
   const current = parseCartCookie(Cookies.get(cartCookieKey({ userId })));
   if (current) {
      return current;
   }
   const retired = parseCartCookie(Cookies.get(retiredCartCookieKey({ userId })));
   if (retired) {
      setCartCookie({ cartCookie: retired, userId });
   }
   return retired;
}

function parseCartCookie(rawCookie: string | undefined): CartCookie | null {
   let json;
   try {
      json = JSON.parse(rawCookie ?? '');
   } catch {
      return null;
   }
   const parsed = CartCookieSchema.safeParse(json);
   return parsed.success ? parsed.data : null;
}

function setCartCookie({ cartCookie, userId }: { cartCookie: CartCookie; userId: number | null }) {
   const domain: string | undefined = ifixitDomain() ?? cominorDomain() ?? undefined;
   // Carts live for 10 days https://shopify.dev/docs/storefronts/headless/building-with-the-storefront-api/cart#limitations-and-considerations
   const expires = 10;
   Cookies.set(cartCookieKey({ userId }), JSON.stringify(cartCookie), { domain, expires });
   Cookies.remove(retiredCartCookieKey({ userId }), { domain });
}

/**
 * Keep in sync with ShopifyCustomerService.php
 */
function cartCookieKey({ userId }: { userId: number | null }) {
   return `shopifyCart-user-${userId ?? 'anonymous'}`;
}

/**
 * The name this cookie used before it moved off the dots that PHP mangles
 * into underscores. Read it once, write it back under the new name, and drop
 * it, so the old name disappears on the visitor's next store page.
 */
function retiredCartCookieKey({ userId }: { userId: number | null }) {
   return `shopifyCart.user.${userId ?? 'anonymous'}`;
}

function cominorDomain() {
   return /\.cominor.com$/.test(window.location.hostname) ? '.cominor.com' : null;
}

function ifixitDomain() {
   return /\.ifixit.com$/.test(window.location.hostname) ? '.ifixit.com' : null;
}
